import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';

import {
  listMessages,
  type PagedMessages,
} from '@/features/chat/chat.api';
import { chatKeys } from '@/features/chat/keys';

/**
 * Page size for the chat history. Deliberately smaller than the Hub's
 * own default (50, see `listMessages`'s `limit` param) — opening a
 * conversation should only pull a lean 20-40 message window so the panel
 * paints fast; older history still pages in transparently via
 * `fetchNextPage` as the operator scrolls up.
 */
const PAGE_SIZE = 30;

/**
 * Infinite list of messages for one conversation.
 *
 *   - Backend orders `created_at DESC` — newest in `pages[0].items[0]`.
 *     The MessageList component flattens + reverses so the viewport
 *     reads oldest-at-top, newest-at-bottom.
 *   - `getNextPageParam` walks the accumulated item count against
 *     `total` (the unpaged count the backend returns on every page) and
 *     stops when there is nothing older to fetch.
 *   - `staleTime: 30s` — `useSendMessage` patches the cache in place on a
 *     200, so a normal send never round-trips through a refetch. Polling
 *     covers the inbound case; it goes away in M5-T6 when the WebSocket
 *     `message.new` event arrives and `setQueryData` patches the cache
 *     directly. See the TODO below.
 *   - `enabled` — only fire once a conversation is selected.
 */

// TODO(M5-T6): drop `refetchInterval` once the WebSocket `message.new`
// event handler is in place. The handler will reuse the same
// `mergeServerMessage` helper exported from `useSendMessage` so an
// optimistic outbound bubble doesn't double up when the echo arrives.
export function useMessages(
  conversationId: string | null,
): UseInfiniteQueryResult<{ pages: PagedMessages[]; pageParams: number[] }, Error> {
  return useInfiniteQuery<
    PagedMessages,
    Error,
    { pages: PagedMessages[]; pageParams: number[] },
    ReturnType<typeof chatKeys.messages>,
    number
  >({
    // Use a stable empty-string key when nothing is selected; `enabled` guards
    // the actual fetch so the value is never read.
    queryKey: chatKeys.messages(conversationId ?? ''),
    queryFn: ({ pageParam }) =>
      listMessages({
        conversationId: conversationId as string,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const consumed = allPages.reduce((sum, p) => sum + p.items.length, 0);
      if (consumed >= lastPage.total) {
        return undefined;
      }
      return consumed;
    },
    enabled: !!conversationId,
    staleTime: 30_000,
    // Keep an inactive conversation's window cached well past react-query's
    // 5-minute default — operators hop between customers constantly, and a
    // GC'd cache made the history "disappear" until a full reload (the WS
    // projection would rebuild the slot with only the newest trickle).
    gcTime: 30 * 60_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}
